import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID, effect, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { filter, skip } from 'rxjs';
import { ChatWidgetComponent } from './components/chat-widget.component';
import { CookieBannerComponent } from './components/cookie-banner.component';
import { FooterComponent } from './components/footer.component';
import { ConsentService } from './services/consent.service';
import { NavbarComponent } from './components/navbar.component';
import { FeaturesService } from './services/features.service';
import { GoogleTagService } from './services/google-tag.service';
import { MetaPixelService } from './services/meta-pixel.service';
import { LanguageService } from './services/language.service';
import { SeoService } from './services/seo.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    TranslateModule,
    NavbarComponent,
    FooterComponent,
    ChatWidgetComponent,
    CookieBannerComponent,
  ],
  template: `
    <div id="page-wrapper">
      <app-navbar />
      <!-- Routed pages render inside <main> so screen readers get the one main
           landmark Lighthouse (and skip-to-content shortcuts) expect. -->
      <main id="content">
        <router-outlet></router-outlet>
      </main>
      <app-footer />
      <!-- La bolla della chat, montata QUI e non nelle pagine: così la conversazione
           non riparte da capo a ogni cambio di rotta. In home no: lì il widget sta
           aperto dentro la sezione "Guardala lavorare", e due widget sono due widget. -->
      @if (floatingChat()) {
        <app-chat-widget />
      }
      <app-cookie-banner />
    </div>
  `,
})
export class App implements OnInit {
  protected readonly floatingChat = signal(false);

  private readonly isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) platformId: object,
    private readonly pixel: MetaPixelService,
    private readonly googleTag: GoogleTagService,
    private readonly features: FeaturesService,
    private readonly router: Router,
    private readonly seo: SeoService,
    // Created first, so the page's language is set before anything is translated.
    private readonly lang: LanguageService,
    private readonly consent: ConsentService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    // Each tag starts only once its purpose is granted (decision D12): at load when the choice
    // is already saved, or the moment the visitor accepts in the banner. Both boots are no-ops
    // where the tag's flag is off and are idempotent.
    if (this.isBrowser && (this.features.metaPixel || this.features.googleTag)) {
      effect(() => {
        if (this.consent.granted('meta')) this.pixel.boot();
        if (this.consent.granted('google')) this.googleTag.boot();
      });
    }
  }

  ngOnInit(): void {
    // Head management (title/description/canonical/hreflang/JSON-LD) runs on BOTH
    // platforms: on the server it puts the tags in the rendered HTML for crawlers,
    // in the browser it keeps them in sync across SPA navigations.
    this.seo.init();
    // Solo sugli eventi di navigazione, non anche subito: qui il router non ha ancora
    // risolto la rotta, `activePage()` risponde null e la home monterebbe la bolla per un
    // istante — scaricando il bundle del widget su una pagina che non lo usa. La prima
    // navigazione chiude comunque prima del render, in SSR e in idratazione.
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        this.syncFloatingChat();
        this.ensureAnchor();
      });
    // NOTE: Bootstrap's JS bundle used to be imported here (~80 KB on every page load).
    // Nothing in the app used it — there is not one `data-bs-*` attribute in the
    // templates and the nav menu is plain Angular state — so
    // the import is gone. Bootstrap's SCSS (reset, grid, utilities) is unaffected.
    // Tag di tracciamento — Meta Pixel e tag Google (Google Ads), entrambi PRODUCTION ONLY.
    // Il boot manda la page view di atterraggio; le navigazioni SPA SUCCESSIVE (la prima è
    // già coperta dal boot) la rimandano a mano. Ogni metodo dei due servizi è già un no-op
    // quando il rispettivo flag è spento: qui si gatea solo per non montare una
    // sottoscrizione al router e un idle callback che non servirebbero a niente.
    if (this.isBrowser && (this.features.metaPixel || this.features.googleTag)) {
      // I due servizi installano SUBITO la propria coda di comandi (fbq, dataLayer) e
      // rimandano da soli il download pesante — fbevents.js sono ~176 KB e ~270 ms di
      // thread principale, gtag/js altri ~90 KB. Prima aspettava tutto, coda compresa:
      // fra il caricamento e quello slot idle `fbq` non esisteva, e un cambio di rotta
      // in quella finestra perdeva la sua PageView senza dire niente a nessuno.
      // La PageView di atterraggio Meta è comunque GIÀ partita server-side (CAPI, vedi
      // server.ts) con lo stesso `window.__metaPvId`: la copia browser deve solo
      // arrivare, non arrivare per prima.
      // The boots wait for the visitor's consent: see the effect in the constructor. Until
      // then `fbq` and `gtag` do not exist and the page views below are no-ops.
      this.router.events
        .pipe(
          filter((e): e is NavigationEnd => e instanceof NavigationEnd),
          skip(1),
        )
        .subscribe(() => {
          this.pixel.trackPageView();
          this.googleTag.trackPageView();
        });
    }
  }

  /** La bolla su tutte le pagine tranne la home, che ha il widget aperto in sezione. */
  private syncFloatingChat(): void {
    this.floatingChat.set(this.seo.activePage()?.key !== 'home');
  }

  /**
   * Porta davvero all'ancoraggio quando si arriva da fuori con l'indirizzo già puntato —
   * `/it/canali#telefono` è l'indirizzo che usiamo nelle campagne e dalla home.
   *
   * `withInMemoryScrolling({ anchorScrolling: 'enabled' })` scorre alla fine della
   * navigazione, che con SSR e caricamento differito arriva prima che la pagina abbia la
   * sua altezza definitiva: il risultato è che si resta in cima. Qui si riprova quando il
   * DOM si è fermato, e solo se nessuno ha già scrollato — così una navigazione normale,
   * o il ritorno indietro con la posizione ripristinata, non viene toccata.
   */
  private ensureAnchor(): void {
    if (!this.isBrowser) return;
    const id = this.router.parseUrl(this.router.url).fragment;
    if (!id) return;
    // Riprova per un paio di secondi se l'elemento non c'è ancora: le sezioni sotto la
    // prima schermata sono blocchi @defer, che su una navigazione interna (la Home che
    // porta a /it/canali#telefono) compaiono un attimo dopo la pagina.
    // Se il router non l'ha trovato, la pagina è rimasta dov'era la precedente (anche
    // migliaia di pixel più giù): quindi si guarda dove sta l'elemento, non se si è in
    // cima. Indietro e avanti del browser no: lì il router ripristina la posizione.
    if (this.router.lastSuccessfulNavigation?.trigger === 'popstate') return;
    let tries = 0;
    const go = (): void => {
      const el = document.getElementById(id);
      if (!el) {
        if (++tries < 20) setTimeout(go, 100);
        return;
      }
      // Già lì (il router ci ha portati: in cima, sotto la barra fissa): niente da fare.
      const top = el.getBoundingClientRect().top;
      if (top >= -8 && top <= 160) return;
      // Istantaneo: `scroll-behavior: smooth` su un salto di qualche migliaio di
      // pixel all'apertura della pagina è una animazione che nessuno ha chiesto.
      el.scrollIntoView({ block: 'start', behavior: 'instant' });
    };
    setTimeout(go);
  }

}
